// =============================================================================
// hero-viewmodel.ts – View-model builder for Z-Transit Cards
// =============================================================================

import type { AppState, AppConfig, Direction, Trip } from "./types.js";
import { getUrgencyState, getReachabilityState, minsToHHMM, getDayType, resolveDirection } from "./utils.js";
import { getStopName, RETURN_DESTINATIONS } from "./line-config.js";
import { STOP_COORDINATES } from "./map-data.js";
import { TRAIN_STATIONS } from "../data/trains.js";
import { buildTrainStationBlock } from "./trains";
import { getWalkMinForStop } from "./walk-per-stop";

export interface HeroViewModel {
  lineId: string;
  direction: Direction;
  depHHMM: string;
  wait: number;
  walkMin: number;
  leaveCountdown: number;
  leaveAtHHMM: string;
  urgency: { label: string; css: string };
  destination: string;
  fromStop: string | null;
  fromStopName: string;
  hasMapPin: boolean;
  upcomingStops?: Array<{
    code: string;
    name: string;
    timeHHMM: string;
    past: boolean;
    upcoming: boolean;
  }>;
  connectionsBlock?: string;
  trainStationBlock?: string;
  recentlyDepartedBlock?: string;
  tripId?: number;
  stopsCount?: number;
  reachability?: { label: string; css: string };
  stopCandidates?: string[];
  preferredStop?: string | null;
  compactStops?: string[];
  trip?: any;
}

/**
 * Builds the pure view-model for rendering the Hero (Featured) Card.
 */
export function buildHeroViewModel(
  card: any,
  direction: Direction,
  cfg: AppConfig,
  currentMin: number,
  state: AppState
): HeroViewModel {
  const trip = card.trip;
  if (!trip) {
    throw new Error("Cannot build hero viewmodel without active trip");
  }

  const wait = (trip._depMin ?? 0) - currentMin;
  const walkMin = getWalkMinForStop(card.fromStop, state, cfg);
  const busState = getUrgencyState(wait);
  const reachability = direction === "outbound" ? getReachabilityState(wait, walkMin) : busState;
  const urgency = busState.css === "missed" ? busState : reachability;

  const returnDest =
    card.config?.returnDestination ||
    (RETURN_DESTINATIONS as Record<string, string>)[card.lineId] ||
    "Busto Garolfo";
  const destination =
    direction === "return"
      ? card.toStop
        ? getStopName(card.toStop)
        : returnDest
      : card.config?.destination || "";

  const fromStop = card.fromStop || null;
  const fromStopName = fromStop ? getStopName(fromStop) : "";
  const hasMapPin = !!(
    fromStop &&
    typeof STOP_COORDINATES !== "undefined" &&
    (STOP_COORDINATES as Record<string, any>)[fromStop]
  );

  return {
    lineId: card.lineId,
    direction,
    depHHMM: minsToHHMM(trip._depMin ?? 0),
    wait,
    walkMin,
    leaveCountdown: wait - walkMin,
    leaveAtHHMM: minsToHHMM((trip._depMin ?? 0) - walkMin),
    urgency,
    reachability,
    destination,
    fromStop,
    fromStopName,
    hasMapPin,
    stopCandidates: card.stopCandidates || [],
    preferredStop: card.preferredStop || null,
    compactStops: card.compactStops || [],
    trip
  };
}

export interface TimeInfoViewModel {
  lineId?: string;
  direction: Direction;
  depHHMM: string;
  wait: number;
  walkMin: number;
  leaveCountdown: number;
  leaveAtHHMM: string;
  fromStop: string | null;
  fromStopName: string;
  destination: string;
  hasMapPin: boolean;
  stopCandidates: string[];
  preferredStop: string | null;
}

export function buildTimeInfoViewModel(
  trip: Trip | null | undefined,
  wait: number | null,
  walkMin: number,
  direction: Direction,
  fromStop: string,
  destination: string,
  card: any
): TimeInfoViewModel | null {
  if (!trip) return null;

  const fromStopName = fromStop ? getStopName(fromStop) : "";
  const hasMapPin = !!(
    fromStop &&
    typeof STOP_COORDINATES !== "undefined" &&
    (STOP_COORDINATES as Record<string, any>)[fromStop]
  );

  return {
    lineId: card?.lineId,
    direction,
    depHHMM: minsToHHMM(trip._depMin ?? 0),
    wait: wait ?? 0,
    walkMin,
    leaveCountdown: (wait ?? 0) - walkMin,
    leaveAtHHMM: minsToHHMM((trip._depMin ?? 0) - walkMin),
    fromStop,
    fromStopName,
    destination,
    hasMapPin,
    stopCandidates: card?.stopCandidates || [],
    preferredStop: card?.preferredStop || null
  };
}

export interface TrainCardViewModel {
  stationCode: string;
  stationName: string;
  stopId?: string;
  reachMinutes: number;
  trainDirection: string;
  isReturn: boolean;
  trains: any[];
  firstTrain: any;
  wait: number;
  urgency: { label: string; css: string };
  depHHMM: string;
  leaveAtHHMM: string;
  isHero: boolean;
}

export function buildTrainCardViewModel(
  stationCode: string,
  state: AppState,
  currentMin: number,
  cfg: AppConfig,
  isHero = false
): TrainCardViewModel | null {
  const stationInfo = (TRAIN_STATIONS as Record<string, any>)[stationCode];
  if (!stationInfo) return null;

  const reachMinutes =
    stationCode === "CN_FS"
      ? Number(state?.settings?.stationReachMinutes?.["CN_FS"] ?? state?.settings?.driveCanegrate ?? cfg.defaults?.stationReachMinutes?.["CN_FS"] ?? 16)
      : Number(state?.settings?.stationReachMinutes?.[stationCode] || 0);

  const now = new Date();
  const dayType = getDayType(now, cfg);

  const baseDirection =
    state?.settings?.liveDirection || cfg?.defaults?.liveDirection || "outbound";
  const resolvedDir = resolveDirection(baseDirection, state?.settings);
  const trainDirection = resolvedDir === "return" ? "from_milano" : "to_milano";
  const isReturn = trainDirection === "from_milano";

  const block = buildTrainStationBlock(
    stationCode,
    reachMinutes,
    currentMin,
    dayType,
    trainDirection
  );
  const trains = block.trains;

  if (!trains.length) return null;

  const firstTrain = trains[0];
  const wait = block.canLeaveIn ?? 0;
  const urgency = getUrgencyState(wait);

  const stationName = stationInfo.name + " FS";
  const depHHMM = minsToHHMM(firstTrain.departureMin);
  const leaveAtHHMM = minsToHHMM(firstTrain.departureMin - reachMinutes);

  return {
    stationCode,
    stationName,
    stopId: stationInfo.stopId,
    reachMinutes,
    trainDirection,
    isReturn,
    trains,
    firstTrain,
    wait,
    urgency,
    depHHMM,
    leaveAtHHMM,
    isHero
  };
}
